import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { connect } from 'react-redux';
import { style as tss } from 'typestyle';
import { Omit } from 'utility-types';

import { EditableTableCell, Table } from '../../../controls';
import { LABELS } from '../../../dictionaries';
import { ExternalOrderContentItemModel } from '../../../models';
import { RootState } from '../../../modules';

import { ContainerActions } from '../../../components';

type ComponentProps = Readonly<{
  pageSize: number;
  itemToParcelMap?: { [key: string]: string };
  onGoToParcel?: (id: string) => void;
  size?: 'middle' | 'small';
  showEmptyMessage?: boolean;
  items: ExternalOrderContentItemModel[];
  onItemDelete?: (index: number) => void;
  onItemUpdate?: (index: number, record: ExternalOrderContentItemModel) => void;
}>;

@observer
class Component extends React.Component<ComponentProps, {}> {
  @observable
  sortingState = {
    order: 'descend' as 'descend',
    columnKey: 'created',
  };

  @observable tempRecords = new Map<number, ExternalOrderContentItemModel>();

  setSortingState = (sorting: typeof Component.prototype.sortingState) => {
    this.sortingState.columnKey = sorting.columnKey;
    this.sortingState.order = sorting.order;
  };

  createEditableCell = (
    index: number,
    record: ExternalOrderContentItemModel,
    prop: keyof ExternalOrderContentItemModel
  ) => {
    const EditableCell = observer(() => {
      return (
        <EditableTableCell
          editable={this.tempRecords.has(index)}
          value={record[prop]}
          onChangeHandler={v => {
            const temp = this.tempRecords.get(index)!;
            this.tempRecords.set(index, { ...temp, [prop]: v });
          }}
        />
      );
    });
    return <EditableCell />;
  };

  createActions = (index: number) => {
    const Actions = observer(() => {
      const normalStateActions = [
        { handler: () => this.handleEditAction(index), label: LABELS.EDIT },
        { handler: () => this.handleRemoveAction(index), label: LABELS.REMOVE },
      ];
      const editStateActions = [
        { handler: () => this.handleSaveAction(index), label: LABELS.SAVE },
        { handler: () => this.handleCancelAction(index), label: LABELS.CANCEL },
      ];

      return (
        <div style={{ display: 'flex' }}>
          <ContainerActions.ActionsGroup
            actionItems={this.tempRecords.has(index) ? editStateActions : normalStateActions}
          />
        </div>
      );
    });
    return <Actions />;
  };

  handleEditAction = (index: number) => {
    this.tempRecords.set(index, this.props.items[index]);
  };

  handleRemoveAction = (index: number) => {
    const { onItemDelete } = this.props;
    if (onItemDelete) {
      onItemDelete(index);
    }
  };

  handleCancelAction = (index: number) => {
    this.tempRecords.delete(index);
  };

  handleSaveAction = (index: number) => {
    const { onItemUpdate } = this.props;
    if (onItemUpdate && this.tempRecords.has(index)) {
      const data = this.tempRecords.get(index)!;
      onItemUpdate(index, data);
    }
    this.tempRecords.delete(index);
  };

  render() {
    const { items, onItemDelete, onItemUpdate, size = 'middle', showEmptyMessage } = this.props;

    return (
      <section>
        <h4 className={styles.header}>
          External Orders ({items.length})<br />
        </h4>
        <Table<ExternalOrderContentItemModel>
          size={size}
          title={() => ''}
          dataSource={items}
          rowKey={record => record.externalId}
          pagination={false}
          onChange={(paging, filtering, sorting) => {
            this.setSortingState(sorting as any);
          }}
          showEmptyMessage={showEmptyMessage}
          emptyMessage={<p>List is empty</p>}
          sortDirections={['ascend', 'descend']}
        >
          <Table.Column<ExternalOrderContentItemModel>
            title="External ID"
            key="name"
            className="t_external-order-id-cell"
            width="100px"
            render={(text, record, index) => this.createEditableCell(index, record, 'externalId')}
          />
          {onItemDelete && onItemUpdate && (
            <Table.Column<ExternalOrderContentItemModel>
              title="Actions"
              key="actions"
              width="100px"
              render={(text, record, index) => this.createActions(index)}
            />
          )}
        </Table>
      </section>
    );
  }
}

const styles = {
  header: tss({
    marginBottom: '-5px',
  }),
};

// Connected Type
type OwnProps = Omit<ComponentProps, 'pageSize'>;

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  pageSize: state.app.pageSize,
});

export const ExternalOrdersTable = connect(mapStateToProps, {})(Component);
ExternalOrdersTable.displayName = 'ExternalOrdersTable';
